import React from "react";
import {
    Stack, Box, Grid, Typography, Button, IconButton,
    Badge, useMediaQuery, Switch
} from "@mui/material";
import { useTheme, styled } from "@mui/material/styles";
import { AddCircle } from "@mui/icons-material";
import { PrimaryButton } from "../buttons";

/**
 * SectionHeader - Заголовок секции с кнопкой действия справа
 * На мобилке кнопка становится иконкой
 * @param {boolean} compact - Уменьшенные отступы (для вложенных секций)
 */
export function SectionHeader({
    title,
    buttonText,
    onButtonClick,
    buttonIcon = <AddCircle fontSize="large" />,
    variant = "h3",
    compact = false
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ my: compact ? { xs: 0.5, sm: 1.5 } : { xs: 2, sm: 3 } }}
        >
            <Typography variant={variant} color="text.primary" fontWeight={600}>
                {title}
            </Typography>

            {onButtonClick && (
                isMobile ? (
                    <IconButton onClick={onButtonClick} color="primary" size="large">
                        {buttonIcon}
                    </IconButton>
                ) : (
                    <PrimaryButton
                        onClick={onButtonClick}
                        fullWidth={false}
                        sx={{ px: 4 }}
                    >
                        {buttonText}
                    </PrimaryButton>
                )
            )}
        </Stack>
    );
}

/**
 * CardGrid - Адаптивная сетка карточек
 */
export function CardGrid({
    items,
    renderItem,
    keyExtractor = (item) => item.id,
    columns = { xs: 12, sm: 6, md: 4, lg: 3 },
    spacing = { xs: 2, md: 3 }
}) {
    return (
        <Grid container spacing={spacing} sx={{ mt: 1 }}>
            {items.map((item, index) => (
                <Grid item key={keyExtractor(item, index)} {...columns}>
                    {renderItem(item, index)}
                </Grid>
            ))}
        </Grid>
    );
}

/**
 * CardGridWithBadge - Сетка карточек с badge
 */
const StyledBadge = styled(Badge)(({ theme }) => ({
    '&.MuiBadge-root': {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        position: 'relative'
    },
    '& .MuiBadge-badge': {
        position: 'absolute',
        right: '8%',
        top: 0,
        border: `2px solid ${theme.palette.background.paper}`,
        padding: '0.5rem 0.75rem',
        borderRadius: '1rem',
        fontSize: theme.typography.badgeSmall.fontSize,
        [theme.breakpoints.up('sm')]: {
            padding: '0.75rem 1rem',
            fontSize: theme.typography.badge.fontSize,
        },
    },
}));

export function CardGridWithBadge({
    items,
    renderItem,
    getBadgeContent,
    badgeColor = "primary",
    keyExtractor = (item) => item.id,
    columns = { xs: 12, sm: 6, md: 4, lg: 3 },
    spacing = { xs: 2, md: 3 }
}) {
    return (
        <Grid container spacing={spacing} sx={{ mt: 1 }}>
            {items.map((item, index) => (
                <Grid item key={keyExtractor(item, index)} {...columns}>
                    <StyledBadge
                        badgeContent={
                            <Typography variant="caption">
                                {getBadgeContent(item, index)}
                            </Typography>
                        }
                        color={badgeColor}
                    >
                        {renderItem(item, index)}
                    </StyledBadge>
                </Grid>
            ))}
        </Grid>
    );
}

/**
 * FormRow - Строка формы: инпут(ы) + кнопка действия
 * На мобилке элементы становятся в колонку, кнопка на всю ширину
 */
export function FormRow({
    children,
    buttonText,
    onButtonClick,
    buttonVariant = "contained",
    buttonColor = "primary",
    buttonMinWidth = "10rem"
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="center"
        >
            {children}
            <Button
                variant={buttonVariant}
                color={buttonColor}
                fullWidth={isMobile}
                onClick={onButtonClick}
                sx={{ minWidth: { sm: buttonMinWidth } }}
            >
                {buttonText}
            </Button>
        </Stack>
    );
}

/**
 * InfoCard - Карточка с заголовком, контентом и описанием
 * fullHeight - растягивает карточку на всю высоту и делает children flex колонкой
 */
export function InfoCard({
    title,
    description,
    children,
    titleVariant = "h4",
    centered = true,
    fullHeight = false
}) {
    return (
        <Box
            sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                ...(fullHeight && {
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                })
            }}
        >
            {title && (
                <Typography
                    variant={titleVariant}
                    color="text.primary"
                    textAlign={centered ? "center" : "left"}
                    gutterBottom
                >
                    {title}
                </Typography>
            )}

            <Box sx={{
                mb: description ? 2 : 0,
                ...(fullHeight && { flex: 1, display: 'flex', flexDirection: 'column' })
            }}>
                {children}
            </Box>

            {description && (
                <Typography
                    variant="body1"
                    textAlign={centered ? "center" : "left"}
                    color="text.secondary"
                >
                    {description}
                </Typography>
            )}
        </Box>
    );
}

/**
 * FormSection - Секция формы с заголовком и серым фоном
 * @param {string} title - Заголовок секции
 * @param {React.ReactNode} action - Элемент действия справа от заголовка
 * @param {React.ReactNode} children - Содержимое секции
 */
export function FormSection({
    title,
    action,
    children
}) {
    return (
        <Box sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.200'
        }}>
            {(title || action) && (
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 2}}>
                    {title && (
                        <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                            {title}
                        </Typography>
                    )}
                    {action}
                </Stack>
            )}
            {children}
        </Box>
    );
}

/**
 * PageSection - Секция страницы с заголовком и контентом
 */
export function PageSection({
    title,
    buttonText,
    onButtonClick,
    children
}) {
    return (
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
            <SectionHeader
                title={title}
                buttonText={buttonText}
                onButtonClick={onButtonClick}
            />
            {children}
        </Box>
    );
}

/**
 * EmptyState - Состояние когда список пуст
 */
export function EmptyState({
    message,
    buttonText,
    onButtonClick,
    icon
}) {
    return (
        <Box
            sx={{
                textAlign: 'center',
                py: { xs: 4, sm: 6 },
                color: 'text.secondary'
            }}
        >
            {icon && <Box sx={{ mb: 2, fontSize: '3rem' }}>{icon}</Box>}
            <Typography variant="h5" gutterBottom>
                {message}
            </Typography>
            {buttonText && onButtonClick && (
                <Button
                    variant="contained"
                    onClick={onButtonClick}
                    sx={{ mt: 2 }}
                >
                    {buttonText}
                </Button>
            )}
        </Box>
    );
}

/**
 * ToggleItem - Элемент списка с иконкой, текстом и Switch
 */
export function ToggleItem({
    icon,
    label,
    description,
    checked = false,
    onChange,
    disabled = false,
    color = "primary",
    compact = false
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: { xs: 1, sm: 1.5 },
                borderRadius: 1,
                backgroundColor: checked ? `${color}.lighter` : 'grey.50',
                border: 1,
                borderColor: checked ? `${color}.light` : 'grey.200',
                transition: 'all 0.2s',
                minHeight: compact ? 'auto' : undefined,
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                {icon && (
                    <Box
                        sx={{
                            color: checked ? `${color}.main` : 'grey.400',
                            display: 'flex',
                            flexShrink: 0,
                            '& > svg': { fontSize: { xs: '1.25rem', sm: '1.5rem' } }
                        }}
                    >
                        {icon}
                    </Box>
                )}
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="body2"
                        fontWeight={checked ? 600 : 500}
                        color={checked ? 'text.primary' : 'text.secondary'}
                        noWrap={compact}
                    >
                        {label}
                    </Typography>
                    {description && !compact && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {description}
                        </Typography>
                    )}
                </Box>
            </Stack>

            <Switch
                checked={checked}
                onChange={onChange}
                disabled={disabled}
                color={color}
                size="small"
                sx={{ flexShrink: 0, ml: 1 }}
            />
        </Box>
    );
}

/**
 * ToggleList - Список ToggleItem элементов
 */
export function ToggleList({
    items,
    onToggle,
    disabled = false,
    spacing = 1,
    columns,
    compact
}) {
    const isCompact = compact ?? !!columns;

    const renderItem = (item) => (
        <ToggleItem
            key={item.id}
            icon={item.icon}
            label={item.label}
            description={item.description}
            checked={item.checked}
            onChange={() => onToggle(item.id, !item.checked)}
            disabled={disabled || item.disabled}
            color={item.color || "primary"}
            compact={isCompact}
        />
    );

    if (columns) {
        const isResponsive = typeof columns === 'object';
        const gridItemProps = isResponsive
            ? Object.fromEntries(
                Object.entries(columns).map(([breakpoint, cols]) => [breakpoint, 12 / cols])
              )
            : { xs: 12, sm: 12 / columns };

        return (
            <Grid container spacing={spacing}>
                {items.map((item) => (
                    <Grid item {...gridItemProps} key={item.id}>
                        {renderItem(item)}
                    </Grid>
                ))}
            </Grid>
        );
    }

    return (
        <Stack spacing={spacing}>
            {items.map((item) => renderItem(item))}
        </Stack>
    );
}
